import type { KBTemplate } from "@/lib/pharmacologyKnowledgeBase";
import { normalize, resolveAlias } from "@/lib/pharmacology/aliasResolver";

export type LibraryCategoryKey =
  | "medicamentos"
  | "comuns"
  | "estimulantes"
  | "depressores"
  | "opioides"
  | "benzodiazepinicos"
  | "antidepressivos"
  | "antipsicoticos"
  | "estabilizadores"
  | "tdah"
  | "dissociativos"
  | "psicodelicos"
  | "empatogenos"
  | "canabinoides"
  | "alcool"
  | "outros";

export type LibraryCategoryDefinition = {
  key: LibraryCategoryKey;
  label: string;
  description: string;
  test: (template: KBTemplate) => boolean;
};

const textOf = (t: KBTemplate) =>
  normalize(
    [
      t.name,
      t.generic_name,
      t.pharmacological_class,
      t.pharmacological_subclass,
      t.clinical_category,
      t.substance_type,
      ...(t.therapeutic_areas ?? []),
      ...(t.match ?? []),
      ...(t.synonyms ?? []),
    ]
      .filter(Boolean)
      .join(" "),
  );

const has = (t: KBTemplate, pattern: RegExp) => pattern.test(textOf(t));

export const LIBRARY_CATEGORIES: LibraryCategoryDefinition[] = [
  {
    key: "medicamentos",
    label: "Medicamentos",
    description: "Psicofármacos e outros medicamentos com ação relevante no SNC.",
    test: (t) => t.substance_type === "medicamento",
  },
  {
    key: "comuns",
    label: "Mais consultados",
    description: "Entidades frequentes no cuidado clínico e em dúvidas de interação.",
    test: (t) =>
      /alcool|cannabis|cocaina|cetamina|mdma|clonazepam|lisdexanfetamina|sertralina|tramadol|quetiapina/.test(
        normalize(t.name),
      ),
  },
  {
    key: "estimulantes",
    label: "Estimulantes",
    description: "Agentes com atividade catecolaminérgica ou estimulante relevante.",
    test: (t) => has(t, /estimul|anfet|cocaina|metilfen|cafeina|dopaminerg/),
  },
  {
    key: "depressores",
    label: "Depressores",
    description: "Sedativos, hipnóticos e outros depressores do sistema nervoso central.",
    test: (t) => has(t, /depressor|sedativ|hipnot|gaba|alcool|benzodiazep/),
  },
  {
    key: "opioides",
    label: "Opioides",
    description: "Agonistas e moduladores opioides, incluindo medicamentos analgésicos.",
    test: (t) => has(t, /opio|morf|codeina|tramadol|oxicod|fentan|metadona|buprenorf/),
  },
  {
    key: "benzodiazepinicos",
    label: "Benzodiazepínicos",
    description: "Benzodiazepínicos e fármacos clinicamente próximos.",
    test: (t) => has(t, /benzodiazep|clonazepam|alprazolam|diazepam|lorazepam|midazolam/),
  },
  {
    key: "antidepressivos",
    label: "Antidepressivos",
    description: "ISRS, IRSN, multimodais, tricíclicos e outras classes antidepressivas.",
    test: (t) => has(t, /antidepress|isrs|irsn|seroton|vortiox|sertralin|fluox|venlaf|buprop/),
  },
  {
    key: "antipsicoticos",
    label: "Antipsicóticos",
    description: "Antipsicóticos típicos, atípicos e moduladores dopaminérgicos.",
    test: (t) => has(t, /antipsicot|quetiap|olanzap|risperid|aripipraz|haloperidol/),
  },
  {
    key: "estabilizadores",
    label: "Estabilizadores do humor",
    description: "Lítio, anticonvulsivantes e agentes usados em estabilização do humor.",
    test: (t) => has(t, /estabiliz|litio|valpro|lamotrigin|carbamazep/),
  },
  {
    key: "tdah",
    label: "TDAH",
    description: "Estimulantes e não-estimulantes utilizados no tratamento do TDAH.",
    test: (t) => has(t, /tdah|lisdex|metilfen|atomox|guanfacin|clonidin/),
  },
  {
    key: "dissociativos",
    label: "Dissociativos",
    description: "Agentes com farmacologia dissociativa, especialmente modulação NMDA.",
    test: (t) => has(t, /dissoci|cetamina|ketamina|ketamine|nmda|dextrometorf/),
  },
  {
    key: "psicodelicos",
    label: "Psicodélicos",
    description: "Psicodélicos clássicos e compostos relacionados.",
    test: (t) => has(t, /psicodel|psyched|lsd|psiloc|triptamin|fenetilamin psicodel/),
  },
  {
    key: "empatogenos",
    label: "Empatógenos",
    description: "Entactógenos/empatógenos e compostos com perfil relacionado.",
    test: (t) => has(t, /empat|entact|mdma|mda|serotonergico liberador/),
  },
  {
    key: "canabinoides",
    label: "Canabinoides",
    description: "Cannabis, THC, CBD e outros canabinoides relevantes.",
    test: (t) => has(t, /cannab|canab|thc|cbd|canabino/),
  },
  {
    key: "alcool",
    label: "Álcool",
    description: "Etanol e contexto farmacológico/toxicológico do consumo de álcool.",
    test: (t) => has(t, /alcool|etanol|ethanol/),
  },
  {
    key: "outros",
    label: "Outros",
    description: "Outras substâncias, metabólitos e interferentes relevantes.",
    test: () => true,
  },
];

export function libraryKey(template: KBTemplate) {
  return normalize(template.normalized_key ?? template.name).replace(/\s+/g, "_");
}

export function templateAliases(template: KBTemplate): string[] {
  return Array.from(
    new Set(
      [
        template.generic_name,
        ...(template.brand_names ?? []),
        ...(template.brands ?? []),
        ...(template.international_brand_names ?? []),
        ...(template.reference_names ?? []),
        ...(template.match ?? []),
        ...(template.synonyms ?? []),
      ]
        .filter(Boolean)
        .map((value) => String(value).trim())
        .filter((value) => normalize(value) !== normalize(template.name)),
    ),
  );
}

export function templateCategories(template: KBTemplate): LibraryCategoryKey[] {
  const matches = LIBRARY_CATEGORIES.filter((category) => category.key !== "outros" && category.test(template)).map(
    (category) => category.key,
  );
  return matches.length ? matches : ["outros"];
}

export function categoryCount(templates: KBTemplate[], key: LibraryCategoryKey) {
  const definition = LIBRARY_CATEGORIES.find((item) => item.key === key);
  if (!definition) return 0;
  if (key === "outros") {
    return templates.filter((template) => templateCategories(template).includes("outros")).length;
  }
  return templates.filter(definition.test).length;
}

export function searchLibrary(templates: KBTemplate[], query: string, limit = 80): KBTemplate[] {
  const q = normalize(query);
  if (!q) return templates.slice(0, limit);

  const aliasRank = new Map<string, number>();
  for (const match of resolveAlias(query, Math.max(limit, 24))) {
    aliasRank.set(libraryKey(match.template), match.score);
  }

  return templates
    .map((template) => {
      const haystack = normalize(
        [
          template.name,
          template.generic_name,
          template.pharmacological_class,
          template.pharmacological_subclass,
          template.clinical_category,
          ...templateAliases(template),
        ]
          .filter(Boolean)
          .join(" "),
      );
      let score = aliasRank.get(libraryKey(template)) ?? 0;
      if (normalize(template.name) === q) score = Math.max(score, 1.2);
      else if (normalize(template.name).startsWith(q)) score = Math.max(score, 1.1);
      else if (haystack.includes(q)) score = Math.max(score, 0.8);
      return { template, score };
    })
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score || a.template.name.localeCompare(b.template.name))
    .slice(0, limit)
    .map((row) => row.template);
}

const FAVORITES_KEY = "clinicalos-library-favorites-v1";
const RECENTS_KEY = "clinicalos-library-recents-v1";

function readStringArray(key: string): string[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((value) => typeof value === "string") : [];
  } catch {
    return [];
  }
}

function writeStringArray(key: string, values: string[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, JSON.stringify(Array.from(new Set(values))));
}

export function getLibraryFavorites() {
  return readStringArray(FAVORITES_KEY);
}

export function setLibraryFavorite(key: string, favorite: boolean) {
  const current = getLibraryFavorites();
  const next = favorite ? [key, ...current.filter((item) => item !== key)] : current.filter((item) => item !== key);
  writeStringArray(FAVORITES_KEY, next);
  return next;
}

export function getLibraryRecents() {
  return readStringArray(RECENTS_KEY);
}

export function rememberLibraryRecent(key: string) {
  const next = [key, ...getLibraryRecents().filter((item) => item !== key)].slice(0, 20);
  writeStringArray(RECENTS_KEY, next);
  return next;
}
